import { type InputValue } from "@msidentity/SISU/components/inputs/input-interface";
import { type ResponseError, ViewId } from "@msidentity/SISU/constants";
import {
  type IFormSubmissionProps,
  useNavigateDirection,
  useProgressIndicator,
} from "@msidentity/SISU/hooks";
import { ChannelType } from "@msidentity/SISU/model/proof";
import { type MemberNameTypeValues, MemberNameType } from "@msidentity/SISU/model/user";
import { trim } from "@msidentity/SISU/utilities/strings-helper";
import { AccountApiErrorCodes } from "../../../constants/api";
import { MaskedOrUnmaskedAliasResponseType } from "../../../utilities/api-helpers/consume-ott/consume-ott-constants";
import { getAccountsForProof } from "../../../utilities/api-helpers/get-accounts-for-proof/get-accounts-for-proof";
import {
  type GetAccountsForProofRequest,
  type GetAccountsForProofResponse,
} from "../../../utilities/api-helpers/get-accounts-for-proof/get-accounts-for-proof-types";
import {
  usernameRecoveryEnterCodeViewStringsFabric,
  usernameRecoveryGenericStringsFabric,
  usernameRecoveryGetAccountsForProofStringsFabric,
} from "../fabric/username-recovery-strings-fabric";
import UsernameRecoveryConfig from "../username-recovery-config";
import { type UsernameRecoveryProofState } from "../username-recovery-context";
import {
  type UsernameRecoveryActions,
  UsernameRecoveryActionType,
} from "../username-recovery-reducer";

/**
 * This method takes the response body from GetAccountsForProof API and translates it into the state object
 * @param responseBody The response body from the GetAccountsForProof API
 * @returns The UsernameRecoveryProofState object
 */
export const translateResponseBody = (
  responseBody: GetAccountsForProofResponse,
): UsernameRecoveryProofState => {
  const {
    aliases: returnedAliases,
    isSignInAllowedProof = false,
    hasAssociatedAccounts = false,
  } = responseBody;
  const unfilteredAliases = returnedAliases || [];
  // Only return aliases with a name
  const aliases = unfilteredAliases.filter((alias) => !!alias.name);
  const hasAliases = aliases.length > 0;

  return {
    aliases,
    isSignInAllowedProof,
    hasAssociatedAccounts: hasAssociatedAccounts && hasAliases,
  };
};

/**
 * Consuming OTT entails posting to GetAccountsForProof SAPI endpoint
 * @param proof phone number or email that user entered to make GetAccountsForProof call
 * @param ott the one time code
 * @param phoneCountryIso country code
 * @param ottChannelType channel type for sending a one time code
 * @param dispatcher dispatch function for updating the state
 * @returns handler for calling GetAccountsForProof.
 */
export const useGetAccountsForProof = (
  proof: string,
  ott: InputValue,
  phoneCountryIso: string,
  ottChannelType: MemberNameTypeValues,
  dispatcher: React.Dispatch<UsernameRecoveryActions>,
) => {
  const navigate = useNavigateDirection();
  const { hideProgressIndicator, showProgressIndicator } = useProgressIndicator();
  let consumeOttChannelType: ChannelType;
  if (ottChannelType === MemberNameType.EASI) {
    consumeOttChannelType = ChannelType.Email;
  } else if (ottChannelType === MemberNameType.Phone) {
    consumeOttChannelType = ChannelType.SMS;
  }
  /* else if (ottChannelType === MemberNameType.Unknown) {
    // MemberNameType.Voice in the future
    consumeOttChannelType = ChannelType.Voice;
  } */

  return async (params: IFormSubmissionProps) => {
    const { accountsForProofUrl, allowProofBasedSignIn } = UsernameRecoveryConfig.instance;

    const consumeOttParams: GetAccountsForProofRequest = {
      oneTimeToken: trim(String(ott)),
      isPhoneProofType: consumeOttChannelType === ChannelType.SMS,
      phoneCountryIso: consumeOttChannelType === ChannelType.SMS ? phoneCountryIso : undefined,
      proof,
    };

    if (allowProofBasedSignIn) {
      consumeOttParams.needsSlt = true;
      consumeOttParams.responseType = MaskedOrUnmaskedAliasResponseType;
    }

    const { errorHandler: setServerError } = params;

    // TODO: Use useFormSubmission here instead and clean up try/catch
    try {
      showProgressIndicator();
      const responseBody = await getAccountsForProof(accountsForProofUrl, consumeOttParams);
      const proofState = translateResponseBody(responseBody);

      dispatcher({
        type: UsernameRecoveryActionType.SetAccountsForProofResult,
        result: proofState,
      });

      if (allowProofBasedSignIn && responseBody.slt) {
        dispatcher({
          type: UsernameRecoveryActionType.SetShortLivedToken,
          shortLivedToken: responseBody.slt,
        });
      }

      hideProgressIndicator();

      // If the user has proof based sign in enabled, render the select alias view
      const targetViewId = allowProofBasedSignIn
        ? ViewId.UsernameRecoverySelectAlias
        : ViewId.UsernameRecoveryResult;
      navigate(ViewId.UsernameRecoveryLegacyCode, targetViewId);
    } catch (err) {
      hideProgressIndicator();

      // TODO: Fix type-casting here
      if (err as ResponseError) {
        const responseError = err as ResponseError;
        const error = responseError?.responseBody?.error;
        const code = typeof error === "string" ? error : error?.code || "";
        switch (code) {
          case AccountApiErrorCodes.ExpiredCredentials:
            setServerError(usernameRecoveryGenericStringsFabric.errorExpiredCreds);
            break;
          case AccountApiErrorCodes.InvalidCode:
          case AccountApiErrorCodes.IncorrectVerificationCode:
            setServerError(usernameRecoveryEnterCodeViewStringsFabric.errorInvalidCode);
            break;
          case AccountApiErrorCodes.TooManyCodesRequested:
            setServerError(usernameRecoveryGetAccountsForProofStringsFabric.errorTriedTooManyTimes);
            break;
          case AccountApiErrorCodes.CodeAlreadyConsumed:
            setServerError(
              usernameRecoveryGetAccountsForProofStringsFabric.errorOttAlreadyConsumed,
            );
            break;
          default:
            setServerError(usernameRecoveryGenericStringsFabric.errorGeneric);
            break;
        }
      }
    }
  };
};
